import { AxiosError, AxiosHeaders, AxiosResponse } from 'axios';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { createTransform } from 'redux-persist';
import autoMergeLevel1 from 'redux-persist/lib/stateReconciler/autoMergeLevel1';
import sessionStorage from 'redux-persist/lib/storage/session';
import { init, Plugin, RematchDispatch, RematchRootState } from '@rematch/core';
import immerPlugin from '@rematch/immer';
import loadingPlugin, { ExtraModelsFromLoading } from '@rematch/loading';
import createPersistPlugin, { getPersistor } from '@rematch/persist';
import selectPlugin from '@rematch/select';

import { api } from './api';
import axiosClient from './axios';
import { models, RootModel } from './models';
import { authInitialState } from './models/auth';
import { consoleInitialState, ConsoleStateType } from './models/console';
import { serviceConsoleInitialState, ServiceConsoleStateType } from './models/serviceConsole';

import { isServiceConsole } from './helpers';

type PersistStateType = ServiceConsoleStateType | ConsoleStateType;

const notAllowedList: Array<string> = [];
const allowedList = ['search', 'showDeletedAllocations'];
const SetTransform = createTransform(
  // transform state on its way to being serialized and persisted.
  (inboundState: PersistStateType) => {
    const filtered = pick(inboundState, allowedList);
    const filteredSearch = omit(filtered.search, notAllowedList);
    const rest = omit(filtered, 'search');

    return {
      search: { ...filteredSearch },
      ...rest,
    };
  },
  // transform state being rehydrated
  (outboundState: PersistStateType) => {
    const ret = isServiceConsole
      ? {
          ...serviceConsoleInitialState,
          search: {
            ...serviceConsoleInitialState.search,
            ...outboundState.search,
          },
          showDeletedAllocations: (outboundState as ServiceConsoleStateType).showDeletedAllocations,
        }
      : {
          ...consoleInitialState,
          search: {
            ...consoleInitialState.search,
            ...outboundState.search,
          },
        };
    return ret;
  },
  // define which reducers this transform gets called for.
  { whitelist: ['serviceConsole', 'console'] }
);

export type FullModel = ExtraModelsFromLoading<RootModel, { type: 'full' }>;
const persistConfig = {
  key: 'root',
  storage: sessionStorage,
  stateReconciler: autoMergeLevel1,
  blacklist: ['auth', 'loading'],
  transforms: [SetTransform],
};

const persistPlugin: Plugin<RootModel, FullModel, Partial<FullModel>> = createPersistPlugin(persistConfig);

export const store = init<RootModel, FullModel>({
  redux: {
    rootReducers: {
      ResetApp: () => ({
        serviceConsole: { ...serviceConsoleInitialState },
        console: { ...consoleInitialState },
        auth: { ...authInitialState },
      }),
    },
  },
  models,
  plugins: [persistPlugin, loadingPlugin({ type: 'full' }), immerPlugin(), selectPlugin()],
});

let csrfToken = '';

axiosClient.interceptors.request.use((requestConfig) => ({
  ...requestConfig,
  headers: {
    ...requestConfig.headers,
    [requestConfig.xsrfHeaderName]: csrfToken,
  } as AxiosHeaders,
}));

axiosClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.headers && response.headers[axiosClient.defaults.xsrfHeaderName]) {
      csrfToken = response.headers[axiosClient.defaults.xsrfHeaderName];
    }
    return response;
  },
  (error: AxiosError) => {
    const { response } = error;

    if (response?.status === 401) {
      response.data = {
        code: 'fe_unauthorized',
        message: 'Unauthorized',
        errors: [],
      };

      if (response.config.url.includes('/auth/me')) {
        if (window.location.pathname === '/') {
          window.location.replace(`${window.fromServer.env.HOST_URL}login`);
        } else {
          window.location.replace(`${window.fromServer.env.HOST_URL}401`);
        }
        store.dispatch({ type: 'ResetApp' });
      } else {
        api.AuthApi.authMeGet().catch((e) => console.error(e));
      }
    }

    if (response && (!response.data || typeof response.data !== 'object')) {
      response.data = {
        code: 'fe_unknown',
        message: 'Unknown error',
        errors: [],
      };
    }

    return Promise.reject(response);
  }
);

export const persistor = getPersistor();
export type Store = typeof store;
export type Dispatch = RematchDispatch<RootModel>;
export type RootState = RematchRootState<RootModel, FullModel>;
